import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Upload, UserRound } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errText, mediaUrl } from "@/lib/api";
import { SeeMore, useSeeMore } from "@/components/maintenance/admin/SeeMore";
import { REQ_STATUS, levelOf, requestDetail, requestTitle } from "@/lib/access";
import { fmtDateTime } from "@/lib/time";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";

export const Avatar = ({ user, size = "h-12 w-12" }) => (
  user?.avatar_media_id
    ? <img src={mediaUrl(user.avatar_media_id)} alt={user.name || "Profile"} className={`${size} shrink-0 rounded-full object-cover`} />
    : <span className={`${size} flex shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-white`}><UserRound className="h-1/2 w-1/2" /></span>
);

const RequestItem = ({ r }) => {
  const [label, cls] = REQ_STATUS[r.status] || REQ_STATUS.pending;
  const detail = requestDetail(r);
  return (
    <li className="flex w-full flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6" data-testid={`my-request-${r.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">{requestTitle(r)}</p>
        <p className="mt-0.5 break-words text-sm" data-testid={`my-request-${r.id}-detail`}>{detail}</p>
        <p className="mt-1 text-xs text-[#57534E]">{(r.type === "folder" || (r.type === "upload" && !r.folder_name)) && <>{levelOf(r.level).label} · </>}{fmtDateTime(r.created_at)}</p>
        {r.reason && <p className="mt-1 text-xs text-[#57534E]">Note: {r.reason}</p>}
      </div>
      <span className={`shrink-0 text-sm font-bold ${cls}`} data-testid={`my-request-${r.id}-status`}>{label}</span>
    </li>
  );
};

export const MyRequests = ({ list }) => {
  const page = useSeeMore(list);
  return (
    <div className="border border-black/15 bg-white" data-testid="my-requests">
      {!list.length ? <p className="p-8 text-center text-sm text-[#57534E]" data-testid="my-requests-empty">You haven't made any requests yet.</p> : (
        <>
          <ul className="divide-y divide-black/5">
            {page.shown.map((r) => <RequestItem key={r.id} r={r} />)}
          </ul>
          <SeeMore {...page} testId="my-requests" />
        </>
      )}
    </div>
  );
};

const OwnerDetails = ({ user, refreshMe }) => {
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", job_title: user?.job_title || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/me", { name: f.name.trim(), email: f.email.trim(), job_title: f.job_title.trim() }); await refreshMe(); toast.success("Your details have been saved"); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-6 space-y-3" data-testid="profile-details-form">
      {[["Full name", "name", "text", "profile-name"], ["Email", "email", "email", "profile-email"]].map(([l, k, type, t]) => (
        <label key={k} className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">{l}</span>
          <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} data-testid={t} /></label>
      ))}
      {f.job_title && <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Job role</span>
        <input type="text" value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} className={field} data-testid="profile-job" /></label>}
      <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Account type</span>
        <input readOnly value="Owner account" className={readOnly} data-testid="profile-account-type-readonly" /></label>
      <button type="submit" disabled={busy || !f.name.trim() || !f.email.trim()} className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="profile-details-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save details</button>
    </form>
  );
};

const readOnly = "w-full min-w-0 border border-black/10 bg-[#F3F2EF] px-3 py-2.5 text-sm text-[#3a3836]";

const ChangeDialog = ({ open, onOpenChange, label, k, type, value, onSent }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [proof, setProof] = useState(null);
  const fileRef = useRef(null);
  const isName = k === "name";
  useEffect(() => { if (open) { setV(""); setProof(null); } return undefined; }, [open]);
  const send = async () => {
    if (isName && !proof) return toast.error("Please upload a document proving your name change");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("field", k); fd.append("value", v.trim()); if (isName) fd.append("proof", proof);
      await api.post("/requests/profile", fd); toast.success("Change request sent"); onOpenChange(false); await onSent();
    }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const changed = v.trim() && v.trim() !== (value || "");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid={`profile-${k}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">Request {label.toLowerCase()} change</DialogTitle>
          <DialogDescription>Current: {value || "blank"}</DialogDescription>
        </DialogHeader>
        <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">New {label.toLowerCase()}</span>
          <input type={type} value={v} onChange={(e) => setV(e.target.value)} className={field} data-testid={`profile-${k}-input`} autoFocus /></label>
      {isName && (
        <div className="border border-dashed border-black/25 bg-[#F3F2EF] p-3" data-testid="profile-name-proof">
          <p className="text-xs font-semibold">Proof of name change (required)</p>
          <p className="mt-0.5 text-xs text-[#57534E]">For example a deed poll, marriage certificate or updated ID.</p>
          <button type="button" onClick={() => fileRef.current?.click()} className="mt-2 flex items-center gap-2 border border-black/20 bg-white px-3 py-2 text-xs font-semibold hover:border-[#0A0A0A]" data-testid="profile-name-proof-upload"><Upload className="h-3.5 w-3.5" />{proof ? "Change document" : "Upload document"}</button>
          {proof && <p className="mt-1.5 break-all text-xs" data-testid="profile-name-proof-file">{proof.name}</p>}
          <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx" hidden onChange={(e) => { setProof(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="profile-name-proof-input" />
        </div>
      )}
        <button type="button" onClick={send} disabled={busy || !changed || (isName && !proof)} className="flex w-full items-center justify-center gap-2 bg-[#0A0A0A] px-4 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-40" data-testid={`profile-${k}-send`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send request</button>
      </DialogContent>
    </Dialog>
  );
};

const StaffField = ({ label, k, type, value, pending, onSent }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`profile-field-${k}`}>
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</span>
      <div className="flex gap-2">
        <input readOnly value={value || ""} className={readOnly} data-testid={`profile-${k}-value`} />
        <button type="button" onClick={() => setOpen(true)} className="shrink-0 bg-[#0A0A0A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#E62314]" data-testid={`profile-${k}-request`}>Request</button>
      </div>
      {pending && <p className="mt-1.5 text-xs font-semibold text-[#B45309]" data-testid={`profile-${k}-pending`}>Waiting for approval: “{pending.new_value || "blank"}”</p>}
      <ChangeDialog open={open} onOpenChange={setOpen} label={label} k={k} type={type} value={value} onSent={onSent} />
    </div>
  );
};

const StaffDetails = ({ user }) => {
  const [pending, setPending] = useState([]);
  const load = useCallback(() => api.get("/requests/profile").then((r) => setPending(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => { load(); return undefined; }, [load]);
  return (
    <div className="mt-6 space-y-4" data-testid="profile-details-requests">
      {[["Name", "name", "text"], ["Email", "email", "email"]].map(([l, k, type]) => (
        <StaffField key={k} label={l} k={k} type={type} value={user?.[k]} pending={pending.find((p) => p.field === k)} onSent={load} />
      ))}
      {user?.job_title && <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Job role</span>
          <input readOnly value={user.job_title} className={readOnly} data-testid="profile-job_title-readonly" /></label>}
        {user?.account_type && <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Account type</span>
          <input readOnly value={user.account_type} className={readOnly} data-testid="profile-account-type-readonly" /></label>}
      <p className="text-xs text-[#57534E]">Name and email changes need approval before they show on your account.</p>
    </div>
  );
};

export const Profile = () => {
  const { user, isAdmin, refreshMe } = useAdmin();
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const upload = async (file) => {
    if (!file) return;
    setBusy("avatar");
    try { const fd = new FormData(); fd.append("file", file); await api.post("/me/avatar", fd); await refreshMe(); toast.success("Profile picture updated"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const change = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error("Use at least 8 characters");
    if (pw.next !== pw.confirm) return toast.error("The new passwords don't match");
    setBusy("pw");
    try { await api.post("/me/password", { current: pw.current, new: pw.next }); toast.success("Password changed"); setPw({ current: "", next: "", confirm: "" }); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(""); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="profile">
      <section className="border border-black/15 bg-white p-6">
        <div className="flex items-center gap-4">
          <Avatar user={user} size="h-20 w-20" />
          <button onClick={() => ref.current?.click()} disabled={busy === "avatar"} className="flex items-center gap-2 border border-black/20 px-4 py-2.5 text-sm font-semibold hover:border-[#0A0A0A]" data-testid="profile-avatar-upload">
            {busy === "avatar" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}Change picture
          </button>
          <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {isAdmin ? <OwnerDetails user={user} refreshMe={refreshMe} /> : <StaffDetails user={user} />}
      </section>
      <form onSubmit={change} className="space-y-3 border border-black/15 bg-white p-6" data-testid="profile-password-form">
        <h3 className="font-display text-xl font-extrabold">Change password</h3>
        <input type="password" autoComplete="current-password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={field} data-testid="profile-pw-current" />
        <input type="password" autoComplete="new-password" placeholder="New password (8+ characters)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={field} data-testid="profile-pw-new" />
        <input type="password" autoComplete="new-password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={field} data-testid="profile-pw-confirm" />
        <button type="submit" disabled={busy === "pw" || !pw.current} className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="profile-pw-submit">{busy === "pw" && <Loader2 className="h-4 w-4 animate-spin" />}Change password</button>
      </form>
    </div>
  );
};
